import React, {ReactNode} from "react";
import Message from "./Message";
import Resizeable, {ResizeableDirection} from "../components/Resizeable";
import i18next from "i18next";
import Translate from "../translation/Translate";
import Icon from "../components/Icon";
import {MessageLogProperties, MessageLogStatus} from "./MessageLogInterface";
import PortalPureComponent from "../components/PortalPureComponent";

/**
 * The definition for a {@link MessageLog} component
 */
export class MessageLogDefinition<P extends MessageLogProperties, S extends MessageLogStatus>
	extends PortalPureComponent<P, S> {

	/**
	 * Constructs an {@link MessageLog} component
	 */
	constructor(props: Readonly<P>) {
		super(props);

		this.state = {
			logHeight: "25%"
		} as S;
	}

	/**
	 * Render the Log list itself with the given {@link MessageLog.props.messages}
	 */
	protected renderList(): ReactNode {
		let messages: Array<Message> = [];
		for (let message of this.props.messages.get()) {
			messages.push(Message.fromJson(message));
		}

		// sort messages by date
		messages = messages.sort((a, b) => {
			let aDate: Date = new Date(a.date);
			let bDate: Date = new Date(b.date);
			return aDate < bDate ? 1 : 0;
		});

		let entries: Array<ReactNode> = [];
		for (let message of messages) {
			let date: Date = new Date(message.date);

			entries.push(
				<tr key={message.id} className={message.type}>
					<td>{date.toLocaleString(i18next.language)}</td>
					<td><Icon icon={"icon-" + message.type}/></td>
					<td><Translate {...this.props} data={message.message} namespace={"message"}
								   values={message.context}/></td>
					<td>{message.code}</td>
				</tr>
			);
		}

		if (entries.length === 0) {
			return (
				<p className="no-results">
					<Translate {...this.props} data="log.empty"/>
				</p>
			);
		} else {
			return (
				<>
					<table className="log-header">
						<thead>
						<tr>
							<th>
								<Translate {...this.props} data="log.date"/>
							</th>
							<th/>
							<th>
								<Translate {...this.props} data="log.message"/>
							</th>
							<th>
								<Translate {...this.props} data="log.code"/>
							</th>
						</tr>
						</thead>
					</table>
					<div className="log-content">
						<table>
							<tbody>{entries}</tbody>
						</table>
					</div>
				</>
			)
		}
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		return (
			<>
				<div className="log-list p-md-hidden" hidden={!this.props.showLog}
					 style={{height: this.state.logHeight}}>
					<Resizeable direction={ResizeableDirection.VERTICAL} container={this.props.resizeContainer}
								resizeCallback={
									(options: { height: string }) => this.setState({logHeight: options.height})
								} invert={true}/>
					{this.renderList()}
				</div>
			</>
		);
	}
}

/**
 * A MessageLog component is used to display the given {@link MessageLog.props.messages} in form of a table.
 */
const MessageLog = React.memo(MessageLogDefinition,
	(prevProps: Readonly<MessageLogProperties>, nextProps: Readonly<MessageLogProperties>) => {
		if (nextProps.showLog !== prevProps.showLog) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		if (!nextProps.messages.equals(prevProps.messages)) {
			return false;
		}

		return true;
	}
);

export default MessageLog;